import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SystemStatusBanner from '../../components/ui/SystemStatusBanner';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SearchBar from './components/SearchBar';
import FilterSidebar from './components/FilterSidebar';
import ResultsGrid from './components/ResultsGrid';
import BulkActionsBar from './components/BulkActionsBar';
import AuditTrailModal from './components/AuditTrailModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import StatsOverview from './components/StatsOverview';
import { supabase } from '../../lib/supabase';

const ShipmentHistorySearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [activeFilters, setActiveFilters] = useState({});
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    delivered: 0,
    inTransit: 0,
    exceptions: 0
  });

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      // Fetch shipments from Supabase - replace with actual table query
      const { data, error } = await supabase?.from('shipments')?.select('*')?.order('timestamp', { ascending: false });

      if (error) throw error;
      setResults(data || []);
      setFilteredResults(data || []);
      
      // Calculate stats from data
      if (data) {
        setStats({
          total: data?.length,
          delivered: data?.filter(s => s?.status === 'Delivered')?.length,
          inTransit: data?.filter(s => s?.status === 'In Transit')?.length,
          exceptions: data?.filter(s => s?.status === 'Exception')?.length
        });
      }
    } catch (error) {
      console.error('Error fetching shipments:', error?.message);
      setResults([]);
      setFilteredResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...results];

    if (searchQuery) {
      filtered = filtered?.filter(item =>
        item?.tracking?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.carrier?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.user?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        const aValue = a?.[sortConfig?.key];
        const bValue = b?.[sortConfig?.key];

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredResults(filtered);
  }, [searchQuery, sortConfig, results]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows(prev =>
      prev?.includes(rowId)
        ? prev?.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? filteredResults?.map(r => r?.id) : []);
  };

  const handleSort = (columnId) => {
    setSortConfig(prev => ({
      key: columnId,
      direction: prev?.key === columnId && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleEdit = (row) => {
    console.log('Edit row:', row);
  };

  const handleDelete = (row) => {
    setDeleteTarget({ type: 'single', data: row });
    setDeleteModalOpen(true);
  };

  const handleViewAudit = (row) => {
    setSelectedShipment(row);
    setAuditModalOpen(true);
  };

  const handleExport = (format) => {
    console.log('Export as:', format);
  };

  const handleStatusUpdate = (status) => {
    console.log('Update status to:', status);
  };

  const handleBatchDelete = () => {
    setDeleteTarget({ type: 'bulk', count: selectedRows?.length });
    setDeleteModalOpen(true);
  };

 const handleConfirmDelete = async () => {
  if (!deleteTarget) return;

  try {
    // 🔹 SINGLE DELETE
    if (deleteTarget.type === 'single') {
      const { error } = await supabase
        .from('shipments')
        .delete()
        .eq('id', deleteTarget.data.id);

      if (error) throw error;

      // 🔹 remove from UI
      setResults(prev => prev.filter(r => r.id !== deleteTarget.data.id));
    }

    // 🔹 BULK DELETE
    if (deleteTarget.type === 'bulk') {
      const { error } = await supabase
        .from('shipments')
        .delete()
        .in('id', selectedRows);

      if (error) throw error;

      // 🔹 remove from UI
      setResults(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    }

    console.log('Delete successful');
  } catch (err) {
    console.error('Delete failed:', err.message);
  } finally {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  }
};


  const handleClearSelection = () => {
    setSelectedRows([]);
  };

  return (
    <>
      <Helmet>
        <title>Shipment History & Search - TrackingManager</title>
        <meta 
          name="description" 
          content="Search and manage shipment history with advanced filtering, bulk operations, and comprehensive audit trails" 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <SystemStatusBanner />

        <main className="pt-[60px]">
          <div className="max-w-screen-2xl mx-auto px-24 py-32">
            <div className="mb-32">
              <h1 className="font-heading font-bold text-h2 text-foreground mb-8">
                Shipment History & Search
              </h1>
              <p className="font-body text-[15px] text-muted-foreground">
                Search, filter, and manage your complete shipment database with advanced tools
              </p>
            </div>

            <StatsOverview stats={stats} />

            <div className="mb-24">
              <SearchBar
                onSearch={handleSearch}
                onFilterToggle={handleFilterToggle}
                isFilterOpen={isFilterOpen}
              />
            </div>

            <div className="mb-18">
              <div className="flex items-center justify-between">
                <div className="font-body text-[14px] text-muted-foreground">
                  Showing {filteredResults?.length?.toLocaleString()} of {stats?.total?.toLocaleString()} results
                </div>
                {selectedRows?.length > 0 && (
                  <div className="font-body text-[14px] text-primary font-medium">
                    {selectedRows?.length} {selectedRows?.length === 1 ? 'item' : 'items'} selected
                  </div>
                )}
              </div>
            </div>

            <ResultsGrid
              results={filteredResults}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              sortConfig={sortConfig}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewAudit={handleViewAudit}
            />
          </div>
        </main>

        <FilterSidebar
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApplyFilters={handleApplyFilters}
          activeFilters={activeFilters}
        />

        <BulkActionsBar
          selectedCount={selectedRows?.length}
          onExport={handleExport}
          onStatusUpdate={handleStatusUpdate}
          onBatchDelete={handleBatchDelete}
          onClearSelection={handleClearSelection}
        />

        <AuditTrailModal
          isOpen={auditModalOpen}
          onClose={() => setAuditModalOpen(false)}
          shipment={selectedShipment}
        />

        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemCount={deleteTarget?.type === 'bulk' ? deleteTarget?.count : 1}
          isBulk={deleteTarget?.type === 'bulk'}
        />

        <QuickActionToolbar />
      </div>
    </>
  );
};

export default ShipmentHistorySearch;